import type { ColumnDef } from "@tanstack/react-table";
import Table, { TableFooter, TableHeading } from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import EmptyState from "@/components/EmptyState";
import { toFormat } from "@/lib/dateHelpers";
import type { CcmsMemberInsuranceRecord } from "../../person/queries";
import { formatCcmsBeneficiaryLineForCell } from "./formatCcmsBeneficiaryLine";

interface CcmsMemberInsuranceTableProps {
  records: CcmsMemberInsuranceRecord[];
  loading?: boolean;
}

type CcmsMemberInsuranceRow = {
  id: string;
  effectiveStartDate: string | null;
  effectiveEndDate: string | null;
  changedAt: string | null;
  author: string | null;
  line1: string | null;
  line2: string | null;
  line3: string | null;
  line4: string | null;
  line5: string | null;
};

const emptyCell = ({ getValue }: { getValue: () => unknown }) => renderEmptyCellState(getValue());

const columns: ColumnDef<CcmsMemberInsuranceRow, unknown>[] = [
  {
    header: "Effective Start",
    id: "effectiveStartDate",
    accessorKey: "effectiveStartDate",
    size: 120,
    cell: emptyCell
  },
  {
    header: "Effective End",
    id: "effectiveEndDate",
    accessorKey: "effectiveEndDate",
    size: 120,
    cell: emptyCell
  },
  {
    header: "Changed",
    id: "changedAt",
    accessorKey: "changedAt",
    size: 180,
    minSize: 160,
    cell: emptyCell
  },
  {
    header: "Author",
    id: "author",
    accessorKey: "author",
    size: 100,
    minSize: 88,
    cell: emptyCell
  },
  {
    header: "Line 1",
    id: "line1",
    accessorKey: "line1",
    size: 240,
    minSize: 180,
    cell: emptyCell
  },
  {
    header: "Line 2",
    id: "line2",
    accessorKey: "line2",
    size: 240,
    minSize: 180,
    cell: emptyCell
  },
  {
    header: "Line 3",
    id: "line3",
    accessorKey: "line3",
    size: 240,
    minSize: 180,
    cell: emptyCell
  },
  {
    header: "Line 4",
    id: "line4",
    accessorKey: "line4",
    size: 240,
    minSize: 180,
    cell: emptyCell
  },
  {
    header: "Line 5",
    id: "line5",
    accessorKey: "line5",
    size: 240,
    minSize: 180,
    cell: emptyCell
  }
];

export default function CcmsMemberInsuranceTable({ records, loading = false }: CcmsMemberInsuranceTableProps) {
  const rows: CcmsMemberInsuranceRow[] = records.map((record) => ({
    id: record.id,
    effectiveStartDate: toFormat(record.effectiveStartDate) ?? null,
    effectiveEndDate: record.effectiveEndDate ? toFormat(record.effectiveEndDate) : null,
    changedAt: record.changedAt ? toFormat(record.changedAt, "LLL dd, yyyy h:mm a") : null,
    author: record.author?.trim() || null,
    line1: formatCcmsBeneficiaryLineForCell(record.benefLine1, record.benefLine1Name, record.benefLine1Relationship),
    line2: formatCcmsBeneficiaryLineForCell(record.benefLine2, record.benefLine2Name, record.benefLine2Relationship),
    line3: formatCcmsBeneficiaryLineForCell(record.benefLine3, record.benefLine3Name, record.benefLine3Relationship),
    line4: formatCcmsBeneficiaryLineForCell(record.benefLine4, record.benefLine4Name, record.benefLine4Relationship),
    line5: formatCcmsBeneficiaryLineForCell(record.benefLine5, record.benefLine5Name, record.benefLine5Relationship)
  }));

  return (
    <>
      <TableHeading
        heading="CCMS Beneficiaries (Member)"
        body="Read-only legacy beneficiary data from CCMS. Each row is one insurance record; long designations may continue across Beneficiary Line 2–5. This data is not editable in Member 360."
      />
      <Table
        data={rows}
        loading={loading}
        columns={columns}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No CCMS Beneficiaries"
            caption="No member insurance beneficiary records exist in CCMS for this person."
            iconName="person"
          />
        )}
        renderFooter={() => <TableFooter totalRowCount={rows.length} />}
        data-testid="ccms-member-insurance-table"
      />
    </>
  );
}
